import { arrayUnion, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * The crew's picture is held until you turn it over (well/product/23 §4). A
 * picture that has come but not been opened is a *print*: it waits face-down,
 * and nothing shows it — not Home, not the film, not your feed — until your
 * tap. Opened pictures are listed on the post (`opened`, by url, which the
 * owner may write under the kindling-posts rules) and remembered on this
 * device too, so the tap counts at once and offline.
 */

/** Pictures on posts made before the hold existed were seen the old way; they are not owed a reveal. */
export const REVEAL_SINCE = Date.UTC(2026, 8, 27);

const KEY = 'kindling.opened';

function openedHere(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(KEY) || '[]') as string[]); } catch { return new Set(); }
}

function rememberHere(url: string): void {
  try {
    const s = openedHere();
    s.add(url);
    localStorage.setItem(KEY, JSON.stringify([...s].slice(-200)));
  } catch { /* private mode: the server copy still holds it */ }
}

export interface Rendered { url: string; idea: string; by?: string }
export interface WithRenders { postId: string; uid?: string; day: number; createdAt: number; renders?: Rendered[]; opened?: string[] }

/** Is this picture still face-down for its owner? */
export function isHeld(post: Pick<WithRenders, 'createdAt' | 'opened'>, url: string, here: Set<string> = openedHere()): boolean {
  if (!url) return false;
  if (post.createdAt && post.createdAt < REVEAL_SINCE) return false;
  return !(post.opened || []).includes(url) && !here.has(url);
}

export interface Print { postId: string; url: string; idea: string; day: number; at: number }

/** Your prints still waiting to be turned over, newest first. */
export function heldPrints(posts: WithRenders[], me?: string): Print[] {
  const here = openedHere();
  const out: Print[] = [];
  for (const p of posts) {
    if (me && p.uid && p.uid !== me) continue;
    for (const r of p.renders || []) if (r.url && isHeld(p, r.url, here)) out.push({ postId: p.postId, url: r.url, idea: (r.idea || '').trim(), day: p.day, at: p.createdAt });
  }
  return out.sort((a, b) => b.at - a.at);
}

/** The same posts with any face-down pictures taken out, for everything that would otherwise show them. */
export function withoutHeld<T extends WithRenders>(posts: T[], me?: string): T[] {
  const here = openedHere();
  return posts.map((p) => {
    if (me && p.uid && p.uid !== me) return p;
    const renders = (p.renders || []).filter((r) => !isHeld(p, r.url, here));
    return renders.length === (p.renders || []).length ? p : { ...p, renders };
  });
}

/** Turned over: remembered here at once, and on the post for every other device. */
export async function markOpened(postId: string, url: string): Promise<void> {
  rememberHere(url);
  try { await updateDoc(doc(db, 'kindling-posts', postId), { opened: arrayUnion(url) }); } catch { /* offline or signed out: this device still knows */ }
}
